<template>
    <glass-dialog :value="value" title="Preferências de publicação" :max-width="560" @input="$emit('input', $event)">
        <div v-if="loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="32" />
        </div>

        <template v-else>
            <div class="field-block mb-4">
                <label class="field-label">Autor padrão <span class="req">*</span></label>
                <v-text-field
                    v-model="defaultAuthor"
                    outlined dense hide-details="auto"
                    placeholder="Nome que aparecerá como autor dos artigos"
                    :error-messages="authorError"
                />
            </div>

            <div class="field-block mb-4">
                <label class="field-label">Imagem padrão <span class="req">*</span></label>
                <div class="field-hint mb-2">Usada quando um artigo automático não tiver imagem própria.</div>
                <ArtigosImagePicker v-model="defaultImage" />
                <div v-if="imageError" class="field-error">{{ imageError }}</div>
            </div>

            <div class="switch-block">
                <div>
                    <div class="switch-title">Publicar sem revisão prévia</div>
                    <div class="switch-hint">
                        Se ligado, artigos que você não aprovar até a data serão publicados automaticamente.
                        Se desligado, eles ficam como rascunho.
                    </div>
                </div>
                <v-switch v-model="publishWithoutReview" color="primary" inset hide-details class="ma-0 pa-0" />
            </div>
        </template>

        <template #actions>
            <v-btn text :disabled="saving" @click="$emit('input', false)">Cancelar</v-btn>
            <v-btn color="primary" :loading="saving" :disabled="loading" @click="save">Salvar</v-btn>
        </template>
    </glass-dialog>
</template>

<script>
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'
import ArtigosImagePicker from '@/components/ArtigosImagePicker.vue'

export default {
    name: 'ArtigosSettingsDialog',
    components: { ArtigosImagePicker },
    props: {
        value: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: false,
            saving: false,
            defaultAuthor: '',
            defaultImage: '',
            publishWithoutReview: false,
            showErrors: false,
        }
    },
    computed: {
        authorError() {
            return this.showErrors && !this.defaultAuthor.trim() ? 'Informe o autor padrão.' : ''
        },
        imageError() {
            return this.showErrors && !String(this.defaultImage || '').trim() ? 'Selecione uma imagem padrão.' : ''
        },
    },
    methods: {
        async load() {
            this.loading = true
            this.showErrors = false
            try {
                const resp = await Api.getArticleSettings()
                if (!resp?.error) {
                    this.defaultAuthor = resp.message.default_article_author || ''
                    this.defaultImage = resp.message.default_article_image || ''
                    this.publishWithoutReview = !!resp.message.publish_without_review
                }
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao carregar configurações.')
            } finally {
                this.loading = false
            }
        },
        async save() {
            if (!this.defaultAuthor.trim() || !String(this.defaultImage || '').trim()) {
                this.showErrors = true
                emitToastr('warning', 'Campos obrigatórios', 'Defina o autor padrão e selecione uma imagem padrão.')
                return
            }
            this.saving = true
            try {
                const resp = await Api.updateArticleSettings({
                    default_article_author: this.defaultAuthor,
                    default_article_image: this.defaultImage,
                    publish_without_review: this.publishWithoutReview,
                })
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao salvar.')
                    return
                }
                emitToastr('success', 'Configurações salvas.')
                this.$emit('saved', resp.message)
                this.$emit('input', false)
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao salvar.')
            } finally {
                this.saving = false
            }
        },
    },
    watch: {
        value(val) {
            if (val) this.load()
        },
    },
}
</script>

<style scoped>
.field-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
}

.req {
    color: var(--color-error, #e5484d);
}

.field-hint {
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--color-on-surface-variant);
}

.field-error {
    font-size: 12.5px;
    color: var(--color-error, #e5484d);
    margin-top: 2px;
}

.switch-block {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
}

.switch-title {
    font-weight: 700;
    font-size: 14px;
    color: var(--color-on-surface);
}

.switch-hint {
    font-size: 12.5px;
    color: var(--color-on-surface-variant);
    margin-top: 2px;
    max-width: 380px;
}
</style>
